import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { RULES_URL, RULE_ACTION_LABELS, humanize, writeHeaders } from "./userVerificationApi";

const EMPTY_RULE = {
  rule_type: "ip_range",
  ip_range: "",
  country: "",
  applies_to: "any",
  action: "mark_suspicious",
  note: "",
};

async function readJson(response, failureMessage) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data.errors || []).join(", ") || data.error || failureMessage);
  return data;
}

function ruleParams(rule) {
  const value = rule.rule_type === "ip_range" ? { ip_range: rule.ip_range } : { country: rule.country };
  return { rule_type: rule.rule_type, applies_to: rule.applies_to, ...value };
}

function CountsCells({ counts }) {
  if (!counts)
    return (
      <td colSpan={2} className="text-muted">
        —
      </td>
    );
  return (
    <>
      <td>
        <strong>{counts.matched}</strong>
        <span className="text-muted small">
          {" "}
          ({counts.media} media, {counts.promoter} promoter)
        </span>
      </td>
      <td className="small">
        {counts.disabled_now} disabled now, {counts.vetted} vetted
      </td>
    </>
  );
}

CountsCells.propTypes = { counts: PropTypes.shape({}) };

function RuleForm({ initialRule, actionOptions, appliesToOptions, countries, onSaved, onCancel }) {
  const [rule, setRule] = useState(initialRule);
  const [error, setError] = useState(null);
  const [draftCounts, setDraftCounts] = useState(null);
  const [busy, setBusy] = useState(false);

  const isCountry = rule.rule_type === "country";
  const update = (field, value) => {
    setDraftCounts(null);
    setRule((current) => ({ ...current, [field]: value }));
  };

  async function countDraft() {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(ruleParams(rule)).forEach(([key, value]) => params.set(`user_verification_rule[${key}]`, value));
      const response = await fetch(`${RULES_URL}/match_counts?${params}`, { headers: authenticatedHeadersHash() });
      const data = await readJson(response, "Could not count matches.");
      setDraftCounts({ ...data.counts[0], onboarded: data.onboarded, window_days: data.window_days });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const url = rule.id ? `${RULES_URL}/${rule.id}` : RULES_URL;
      const body = { ...ruleParams(rule), action: rule.action, enabled: rule.enabled ?? true, note: rule.note };
      const response = await fetch(url, {
        method: rule.id ? "PATCH" : "POST",
        headers: writeHeaders(),
        body: JSON.stringify({ user_verification_rule: body }),
      });
      await readJson(response, "Could not save the rule.");
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card card-body mb-3" onSubmit={save}>
      <div className="row g-2">
        <div className="col-md-2">
          <label className="form-label small" htmlFor="uv-rule-type">
            Type
          </label>
          <select
            id="uv-rule-type"
            className="form-select form-select-sm"
            value={rule.rule_type}
            disabled={Boolean(rule.id)}
            onChange={(event) => update("rule_type", event.target.value)}
          >
            <option value="ip_range">IP range</option>
            <option value="country">Country</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small" htmlFor="uv-rule-value">
            {isCountry ? "Country (as Ahoy records it)" : "IP range (CIDR; one address is /32)"}
          </label>
          {isCountry ? (
            <>
              <input
                id="uv-rule-value"
                className="form-control form-control-sm"
                list="uv-countries"
                value={rule.country || ""}
                onChange={(event) => update("country", event.target.value)}
              />
              <datalist id="uv-countries">
                {countries.map((country) => (
                  <option key={country} value={country} aria-label={country} />
                ))}
              </datalist>
            </>
          ) : (
            <input
              id="uv-rule-value"
              className="form-control form-control-sm"
              placeholder="10.20.0.0/16"
              value={rule.ip_range || ""}
              onChange={(event) => update("ip_range", event.target.value)}
            />
          )}
        </div>
        <div className="col-md-2">
          <label className="form-label small" htmlFor="uv-rule-scope">
            Applies to
          </label>
          <select
            id="uv-rule-scope"
            className="form-select form-select-sm"
            value={rule.applies_to}
            onChange={(event) => update("applies_to", event.target.value)}
          >
            {appliesToOptions.map((scope) => (
              <option key={scope} value={scope}>
                {humanize(scope)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small" htmlFor="uv-rule-action">
            Action
          </label>
          <select
            id="uv-rule-action"
            className="form-select form-select-sm"
            value={rule.action}
            onChange={(event) => update("action", event.target.value)}
          >
            {actionOptions.map((action) => (
              <option key={action} value={action}>
                {RULE_ACTION_LABELS[action] || humanize(action)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label small" htmlFor="uv-rule-note">
            Note
          </label>
          <input
            id="uv-rule-note"
            className="form-control form-control-sm"
            value={rule.note || ""}
            onChange={(event) => update("note", event.target.value)}
          />
        </div>
      </div>
      {draftCounts && (
        <div className="small mt-2">
          Matches {draftCounts.matched} of {draftCounts.onboarded} users onboarded in the last {draftCounts.window_days}{" "}
          days ({draftCounts.media} media, {draftCounts.promoter} promoter; {draftCounts.disabled_now} disabled now,{" "}
          {draftCounts.vetted} vetted).
        </div>
      )}
      {error && <div className="text-danger small mt-2">{error}</div>}
      <div className="d-flex gap-2 mt-3">
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
          Save
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={countDraft}>
          Count matches
        </button>
        <button type="button" className="btn btn-sm btn-link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

RuleForm.propTypes = {
  initialRule: PropTypes.shape({}).isRequired,
  actionOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  appliesToOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

// The rules editor for automated user verification: list, create, edit, enable or
// disable, delete, and count what each rule matches among users onboarded recently.
function UserVerificationRulesPanel({ actionOptions, appliesToOptions }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // undefined = form closed; otherwise the rule being created or edited.
  const [editing, setEditing] = useState(undefined);
  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    fetch(RULES_URL, { headers: authenticatedHeadersHash() })
      .then((response) => readJson(response, "Could not load the rules."))
      .then((data) => {
        setRules(data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  useEffect(() => {
    if (editing === undefined || countries.length) return;
    fetch(`${RULES_URL}/countries`, { headers: authenticatedHeadersHash() })
      .then((response) => readJson(response, "Could not load countries."))
      .then(setCountries)
      .catch(() => {});
  }, [editing, countries.length]);

  async function loadCounts() {
    setCountsLoading(true);
    try {
      const response = await fetch(`${RULES_URL}/match_counts`, { headers: authenticatedHeadersHash() });
      const data = await readJson(response, "Could not count matches.");
      setCounts({ ...data, byRule: Object.fromEntries(data.counts.map((c) => [c.rule_id, c])) });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCountsLoading(false);
    }
  }

  async function write(url, options, failureMessage) {
    try {
      const response = await fetch(url, { ...options, headers: writeHeaders() });
      if (!response.ok) await readJson(response, failureMessage);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  const toggleEnabled = (rule) =>
    write(
      `${RULES_URL}/${rule.id}`,
      { method: "PATCH", body: JSON.stringify({ user_verification_rule: { enabled: !rule.enabled } }) },
      "Could not update the rule.",
    );

  const destroy = (rule) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the rule for ${rule.label}?`)) return;
    write(`${RULES_URL}/${rule.id}`, { method: "DELETE" }, "Could not delete the rule.");
  };

  const onSaved = () => {
    setEditing(undefined);
    setCounts(null);
    load();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
        <p className="text-muted small mb-0">
          Mark suspicious lists the signup on the Suspicious tab and changes nothing for the user. Set under audit puts
          the new user under audit and sends them to the Audited tab.
        </p>
        <div className="d-flex gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary text-nowrap"
            onClick={loadCounts}
            disabled={countsLoading}
          >
            {countsLoading ? "Counting…" : "Count matches (last 30 days)"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary text-nowrap"
            onClick={() => setEditing({ ...EMPTY_RULE })}
          >
            New rule
          </button>
        </div>
      </div>

      {editing !== undefined && (
        <RuleForm
          key={editing.id || "new"}
          initialRule={editing}
          actionOptions={actionOptions}
          appliesToOptions={appliesToOptions}
          countries={countries}
          onSaved={onSaved}
          onCancel={() => setEditing(undefined)}
        />
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {counts && (
        <div className="text-muted small mb-2">
          {counts.onboarded} users onboarded in the last {counts.window_days} days. The IP is the latest sign-in when
          the user has one, so for users who signed in again it may not be their signup IP. Country is the signup
          visit&apos;s.
        </div>
      )}

      {loading && <div className="text-muted">Loading rules…</div>}
      {!loading && rules.length === 0 && <div className="text-muted">No rules yet.</div>}
      {rules.length > 0 && (
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Applies to</th>
              <th>Action</th>
              <th>Enabled</th>
              <th>Note</th>
              <th>Matches</th>
              <th>Of those</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id} className={rule.enabled ? "" : "text-muted"}>
                <td>
                  <span className="badge bg-light text-dark border me-1">{humanize(rule.rule_type)}</span>
                  <code>{rule.label}</code>
                </td>
                <td>{humanize(rule.applies_to)}</td>
                <td>
                  <span className={`badge ${rule.action === "set_under_audit" ? "bg-danger" : "bg-warning text-dark"}`}>
                    {RULE_ACTION_LABELS[rule.action] || humanize(rule.action)}
                  </span>
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={rule.enabled}
                    aria-label={`Enabled: ${rule.label}`}
                    onChange={() => toggleEnabled(rule)}
                  />
                </td>
                <td className="small">{rule.note}</td>
                <CountsCells counts={counts?.byRule[rule.id]} />
                <td className="text-end text-nowrap">
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    onClick={() => setEditing({ ...rule, ip_range: rule.ip_range || "", country: rule.country || "" })}
                  >
                    Edit
                  </button>
                  <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => destroy(rule)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

UserVerificationRulesPanel.propTypes = {
  actionOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  appliesToOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default UserVerificationRulesPanel;
